export default class AuthenticationFetcher {
  constructor({httpFetcher, repository, getCurrentUserService, getAuthenticationTokenService, config}) {
    this._httpFetcher = httpFetcher
    this._repository = repository
    this._getCurrentUserService = getCurrentUserService
    this._getAuthenticationTokenService = getAuthenticationTokenService
    this._config = config
  }

  _getOptionsWithAuthorization = async options => {
    const {BEARER} = this._config.get('REQUEST_HEADERS')
    const authenticationToken = await this._getAuthenticationTokenService.execute()

    return {
      ...options,
      headers: {
        ...options.headers,
        ...(authenticationToken && {Authorization: authenticationToken, [BEARER]: authenticationToken})
      }
    }
  }

  _hasNonValidResponse = error => {
    const nonValidResponses = [401, 403]
    const errorMessages = ['Network Error']

    return error.response
      ? nonValidResponses.includes(error.response.status)
      : errorMessages.some(message => error.message.includes(message))
  }

  /**
   * Tries a the fetch request and retries it with
   * a new token if the request was rejected because of expired token
   */
  _repeatIfHasNonValidResponse = async ({options, fetcher}) => {
    const userEntity = await this._getCurrentUserService.execute()

    return fetcher(await this._getOptionsWithAuthorization(options)).catch(async error => {
      if (this._hasNonValidResponse(error)) {
        // Non valid response means that saved token is not valid anymore.
        this._repository.removeAuthenticationToken({
          userId: userEntity.getId()
        })
        // Repeat the fetch after updating the token.
        return fetcher(await this._getOptionsWithAuthorization(options))
      } else {
        throw error
      }
    })
  }

  async get(url, options = {}) {
    return this._repeatIfHasNonValidResponse({
      options,
      fetcher: optionsWithAuthorization => this._httpFetcher.get(url, optionsWithAuthorization)
    })
  }

  async patch(url, body, options = {}) {
    return this._repeatIfHasNonValidResponse({
      options,
      fetcher: optionsWithAuthorization => this._httpFetcher.patch(url, body, optionsWithAuthorization)
    })
  }

  async post(url, body, options = {}) {
    return this._repeatIfHasNonValidResponse({
      options,
      fetcher: optionsWithAuthorization => this._httpFetcher.post(url, body, optionsWithAuthorization)
    })
  }

  async put(url, body, options = {}) {
    return this._repeatIfHasNonValidResponse({
      options,
      fetcher: optionsWithAuthorization => this._httpFetcher.put(url, body, optionsWithAuthorization)
    })
  }

  async delete(url, options = {}) {
    return this._repeatIfHasNonValidResponse({
      options,
      fetcher: optionsWithAuthorization => this._httpFetcher.delete(url, optionsWithAuthorization)
    })
  }
}
